import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

/**
 * Global drill-down filter (Jun 03 2026)
 * ─────────────────────────────────────────────────────────────────────────
 * One app-wide "I'm looking at this warehouse / customer" selection. Set it by
 * clicking a warehouse or customer card on the Dashboard; every data module
 * (Inventory, Inbound, Outbound, Expiry, Alert Rules, Halley AI, Reports…)
 * then shows ONLY that warehouse/customer end-to-end.
 *
 * How it propagates without touching every page: the API client
 * (services/api.ts) reads `getGlobalFilter()` and injects `warehouses=` /
 * `customers=` query params into every data GET. The backend already
 * intersects those with the caller's authorized scope via
 * effective_warehouses()/effective_customers() — so this is a NARROWING filter
 * on top of scope (a customer can never use it to see another tenant; an admin
 * uses it to focus on one site). Fail-closed is preserved.
 */
export interface GlobalFilter {
  warehouse: string | null;
  customer: string | null;
}

const STORAGE_KEY = 'hala.globalFilter';

// Module-level mirror so non-React code (the axios client) can read the current
// selection synchronously without a hook. Initialised from localStorage so the
// very first request after a reload already carries the filter.
let _current: GlobalFilter = { warehouse: null, customer: null };
try {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw) {
    const p = JSON.parse(raw);
    _current = { warehouse: p?.warehouse || null, customer: p?.customer || null };
  }
} catch { /* ignore */ }

/** Synchronous read of the active filter — used by services/api.ts. */
export function getGlobalFilter(): GlobalFilter {
  return _current;
}

interface GlobalFilterCtx extends GlobalFilter {
  active: boolean;
  setWarehouse: (w: string | null) => void;
  setCustomer: (c: string | null) => void;
  setFilter: (f: Partial<GlobalFilter>) => void;
  clear: () => void;
}

const Ctx = createContext<GlobalFilterCtx | null>(null);

export const GlobalFilterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [filter, setFilterState] = useState<GlobalFilter>(_current);

  useEffect(() => {
    _current = filter;
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(filter)); } catch { /* ignore */ }
    // Drop cached GET responses so the next fetch re-runs with the new filter
    // params (the cache key includes them, so this is belt-and-suspenders for
    // any page already holding a prior-filter response).
    try {
      // Lazy import to avoid a circular dependency at module load.
      import('../services/apiCache').then(m => m.invalidateByPrefix?.('')).catch(() => {});
    } catch { /* ignore */ }
  }, [filter]);

  const setWarehouse = useCallback((w: string | null) => setFilterState(f => ({ ...f, warehouse: w })), []);
  const setCustomer = useCallback((c: string | null) => setFilterState(f => ({ ...f, customer: c })), []);
  const setFilter = useCallback((p: Partial<GlobalFilter>) => setFilterState(f => ({ ...f, ...p })), []);
  const clear = useCallback(() => setFilterState({ warehouse: null, customer: null }), []);

  const value: GlobalFilterCtx = {
    ...filter,
    active: !!(filter.warehouse || filter.customer),
    setWarehouse,
    setCustomer,
    setFilter,
    clear,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export function useGlobalFilter(): GlobalFilterCtx {
  const ctx = useContext(Ctx);
  if (!ctx) {
    // Defensive fallback so a component rendered outside the provider doesn't
    // crash — it just behaves as "no filter".
    return {
      warehouse: null, customer: null, active: false,
      setWarehouse: () => {}, setCustomer: () => {}, setFilter: () => {}, clear: () => {},
    };
  }
  return ctx;
}
